import { useMemo, useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createCollegePlan,
  updateCollegePlan,
} from "../../lib/api-modules/college-plans";
import type {
  CollegePlan,
  CreateCollegePlanInput,
  FamilyMember,
} from "../../lib/api-modules/types";
import { ApiError } from "../../lib/api";
import { centsToDollars, dollarsToCents } from "../../lib/util/money";
import { ErrorMessage } from "../auth/error-message";

/**
 * College plan form — Phase 5 Session 5. Inline create + edit.
 * Defaults assume realistic figures: 5% growth, 5% inflation, 4-year
 * college, beneficiary's DOB-year + 18 as start year (caller pre-seeds).
 */
export function CollegePlanForm({
  mode,
  plan,
  members,
  onClose,
}: {
  readonly mode: "create" | "edit";
  readonly plan?: CollegePlan;
  readonly members: FamilyMember[];
  readonly onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const isEdit = mode === "edit" && plan;

  // Default the beneficiary to the youngest member if creating.
  const defaultBeneficiary = useMemo(() => {
    if (isEdit) return plan.beneficiary_member_id;
    const withDob = members.filter((m) => m.date_of_birth && !m.is_archived);
    if (withDob.length === 0) return members[0]?.member_id ?? "";
    withDob.sort((a, b) =>
      (b.date_of_birth ?? "").localeCompare(a.date_of_birth ?? ""),
    );
    return withDob[0]!.member_id;
  }, [isEdit, plan, members]);

  const [name, setName] = useState<string>(
    plan?.name ?? "Daughter's college fund",
  );
  const [beneficiaryMemberId, setBeneficiaryMemberId] = useState<string>(
    defaultBeneficiary,
  );
  const [targetYear, setTargetYear] = useState<string>(() => {
    if (isEdit) return String(plan.target_college_start_year);
    const beneficiary = members.find((m) => m.member_id === defaultBeneficiary);
    if (beneficiary?.date_of_birth) {
      const dobYear = Number(beneficiary.date_of_birth.slice(0, 4));
      if (Number.isFinite(dobYear)) return String(dobYear + 18);
    }
    return String(new Date().getUTCFullYear() + 18);
  });
  const [collegeYears, setCollegeYears] = useState<string>(
    String(plan?.college_years ?? 4),
  );
  const [costToday, setCostToday] = useState<string>(
    plan ? centsToDollars(plan.target_cost_today_cents) : "30000",
  );
  const [costInflation, setCostInflation] = useState<string>(
    String(plan?.cost_inflation_pct ?? 5),
  );
  const [growthRate, setGrowthRate] = useState<string>(
    String(plan?.growth_rate_pct ?? 5),
  );
  const [monthlyContrib, setMonthlyContrib] = useState<string>(
    plan?.monthly_contribution_cents !== undefined
      ? centsToDollars(plan.monthly_contribution_cents)
      : "",
  );
  const [notes, setNotes] = useState<string>(plan?.notes ?? "");
  const [inlineError, setInlineError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      if (name.trim().length === 0) throw new Error("Name required");
      if (!beneficiaryMemberId) throw new Error("Beneficiary required");
      const targetYearNum = Number(targetYear);
      if (!Number.isInteger(targetYearNum) || targetYearNum < 2020)
        throw new Error("Target year must be a year ≥ 2020");
      const yearsNum = Number(collegeYears);
      if (!Number.isInteger(yearsNum) || yearsNum < 1 || yearsNum > 8)
        throw new Error("College years must be 1–8");
      const costCents = dollarsToCents(costToday);
      if (costCents <= 0)
        throw new Error("Today's-dollar cost must be positive");
      const inflationNum = Number(costInflation);
      const growthNum = Number(growthRate);
      const trimmedNotes = notes.trim();
      const monthlyCents =
        monthlyContrib.trim().length > 0
          ? dollarsToCents(monthlyContrib)
          : undefined;
      const body: CreateCollegePlanInput = {
        name: name.trim(),
        beneficiary_member_id: beneficiaryMemberId,
        target_college_start_year: targetYearNum,
        college_years: yearsNum,
        target_cost_today_cents: costCents,
        cost_inflation_pct: inflationNum,
        growth_rate_pct: growthNum,
        ...(monthlyCents !== undefined
          ? { monthly_contribution_cents: monthlyCents }
          : {}),
        ...(trimmedNotes.length > 0 ? { notes: trimmedNotes } : {}),
      };
      if (isEdit && plan) {
        return updateCollegePlan(plan.plan_id, body);
      }
      return createCollegePlan(body);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["college-plans"] });
      onClose();
    },
    onError: (err) => {
      setInlineError(
        err instanceof ApiError
          ? `${err.message} (code: ${err.code})`
          : err instanceof Error
            ? err.message
            : "Couldn't save plan.",
      );
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setInlineError(null);
    mutation.mutate();
  }

  const activeMembers = members.filter((m) => !m.is_archived);

  return (
    <form
      onSubmit={onSubmit}
      aria-label={isEdit ? "Edit college plan" : "New college plan"}
      className="grid grid-cols-1 gap-3 rounded-md border border-zinc-200 p-4 dark:border-zinc-800 sm:grid-cols-2"
    >
      <header className="sm:col-span-2 flex items-center justify-between">
        <h3 className="text-base font-semibold">
          {isEdit ? `Edit "${plan?.name ?? ""}"` : "New college plan"}
        </h3>
        <button
          type="button"
          onClick={onClose}
          disabled={mutation.isPending}
          className="text-sm text-zinc-500 hover:underline"
        >
          Cancel
        </button>
      </header>

      <label className="flex flex-col gap-1 text-sm sm:col-span-2">
        Name
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          disabled={mutation.isPending}
          className="rounded-md border border-zinc-300 px-3 py-2 dark:border-zinc-700 dark:bg-zinc-900"
        />
      </label>
      <label className="flex flex-col gap-1 text-sm sm:col-span-2">
        Beneficiary
        <select
          value={beneficiaryMemberId}
          onChange={(e) => setBeneficiaryMemberId(e.target.value)}
          disabled={mutation.isPending}
          required
          className="rounded-md border border-zinc-300 px-3 py-2 dark:border-zinc-700 dark:bg-zinc-900"
        >
          <option value="">— Select —</option>
          {activeMembers.map((m) => (
            <option key={m.member_id} value={m.member_id}>
              {m.name}
              {m.date_of_birth ? ` (DOB ${m.date_of_birth})` : ""}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-1 text-sm">
        College start year
        <input
          type="number"
          min="2020"
          max="2080"
          value={targetYear}
          onChange={(e) => setTargetYear(e.target.value)}
          required
          disabled={mutation.isPending}
          className="rounded-md border border-zinc-300 px-3 py-2 dark:border-zinc-700 dark:bg-zinc-900"
        />
      </label>
      <label className="flex flex-col gap-1 text-sm">
        College years
        <input
          type="number"
          min="1"
          max="8"
          value={collegeYears}
          onChange={(e) => setCollegeYears(e.target.value)}
          required
          disabled={mutation.isPending}
          className="rounded-md border border-zinc-300 px-3 py-2 dark:border-zinc-700 dark:bg-zinc-900"
        />
      </label>
      <label className="flex flex-col gap-1 text-sm">
        Today's annual cost ($)
        <input
          type="number"
          step="0.01"
          min="1"
          value={costToday}
          onChange={(e) => setCostToday(e.target.value)}
          required
          disabled={mutation.isPending}
          className="rounded-md border border-zinc-300 px-3 py-2 dark:border-zinc-700 dark:bg-zinc-900"
        />
      </label>
      <label className="flex flex-col gap-1 text-sm">
        Cost inflation (%/yr)
        <input
          type="number"
          step="0.1"
          value={costInflation}
          onChange={(e) => setCostInflation(e.target.value)}
          disabled={mutation.isPending}
          className="rounded-md border border-zinc-300 px-3 py-2 dark:border-zinc-700 dark:bg-zinc-900"
        />
      </label>
      <label className="flex flex-col gap-1 text-sm">
        Growth rate (%/yr)
        <input
          type="number"
          step="0.1"
          value={growthRate}
          onChange={(e) => setGrowthRate(e.target.value)}
          disabled={mutation.isPending}
          className="rounded-md border border-zinc-300 px-3 py-2 dark:border-zinc-700 dark:bg-zinc-900"
        />
      </label>
      <label className="flex flex-col gap-1 text-sm">
        Monthly contribution ($)
        <input
          type="number"
          step="0.01"
          value={monthlyContrib}
          onChange={(e) => setMonthlyContrib(e.target.value)}
          disabled={mutation.isPending}
          className="rounded-md border border-zinc-300 px-3 py-2 dark:border-zinc-700 dark:bg-zinc-900"
        />
      </label>
      <label className="flex flex-col gap-1 text-sm sm:col-span-2">
        Notes
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={mutation.isPending}
          rows={2}
          className="rounded-md border border-zinc-300 px-3 py-2 dark:border-zinc-700 dark:bg-zinc-900"
        />
      </label>

      <div className="sm:col-span-2 flex items-center gap-2">
        <button
          type="submit"
          disabled={mutation.isPending}
          className="rounded-md bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {mutation.isPending
            ? "Saving…"
            : isEdit
              ? "Save changes"
              : "Create plan"}
        </button>
      </div>
      {inlineError && (
        <div className="sm:col-span-2">
          <ErrorMessage>{inlineError}</ErrorMessage>
        </div>
      )}
    </form>
  );
}

